import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DesktopBridge } from "@typedoc/contracts";
import type { Attempt, ConnectionApp } from "@typedoc/contracts/connections";
import { Button, Dialog, Loader } from "@typedoc/ui";
import { ErrorNotice } from "../error-notice";
import { normalizeAppError } from "../../lib/app-error";
import { pollAttempt } from "../../lib/connections/api";
import { listenForAttempt } from "../../lib/connections/attempt-signal";
import type { ConnectionsContext } from "../../lib/connections/use-connections-scope";
import { AppMark, cx } from "./parts";

/**
 * The pieces adding an account and renewing one share: opening the provider's page, waiting for
 * it, and what an older desktop shell that cannot open it says instead.
 */

function currentDesktop(): DesktopBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktop;
}

/**
 * The desktop shell's way to the system browser, when it has one (bridge version 2). Checked by
 * the function itself: an installed shell may be older than the page it loads.
 */
function desktopOpener(desktop: unknown): ((url: string) => void) | undefined {
  const open = (desktop as Partial<DesktopBridge> | undefined)?.openSignIn;
  return typeof open === "function" ? open : undefined;
}

/**
 * Where the provider should send the person back: in a shell that signs in through the system
 * browser, to a page that only says to go back to the app. Otherwise the default callback.
 */
export function signInReturn(desktop: unknown = currentDesktop()): "desktop" | undefined {
  return desktopOpener(desktop) ? "desktop" : undefined;
}

/**
 * The provider's page in a small window of its own, so this one stays where it was. `noopener`
 * keeps the provider's page from reaching back into this one. It also means the browser never
 * says whether the window opened, so the waiting step always offers a link, and the sign-in window
 * reports back over a same-origin channel, with polling behind it. In the desktop shell the page
 * opens in the system browser instead, and polling alone brings the answer back.
 */
export const SIGN_IN_WINDOW = "noopener,popup,width=520,height=720";
export function openSignIn(url: string, desktop: unknown = currentDesktop()) {
  const open = desktopOpener(desktop);
  if (open) open(url);
  else window.open(url, "_blank", SIGN_IN_WINDOW);
}

/**
 * A desktop shell older than bridge version 2 refuses new windows and has no way to the system
 * browser, so a sign-in started there could never be finished. Returns what to tell the person
 * instead, or null where signing in works. The address is the page's own origin, which in the
 * shell is the hosted web app.
 */
export function signInUnavailable(
  desktop: unknown = currentDesktop(),
  origin: string = typeof location === "undefined" ? "" : location.origin,
): { message: string; address: string | null } | null {
  if (!desktop || desktopOpener(desktop)) return null;
  return {
    message: "Adding or renewing an account works in the browser for now.",
    address: /^https?:\/\//.test(origin) ? origin : null,
  };
}

export function DesktopNotice({
  notice,
  className,
}: {
  notice: { message: string; address: string | null };
  className?: string | undefined;
}) {
  return (
    <output
      className={cx(
        "block rounded-xl border border-border bg-secondary px-4 py-3 text-[13px]/5 text-body-foreground",
        className,
      )}
    >
      {notice.message}
      {notice.address && (
        <>
          {" "}
          Open <span className="font-mono text-xs break-all select-all">{notice.address}</span>{" "}
          there.
        </>
      )}
    </output>
  );
}

export function CloseButton({ disabled = false }: { disabled?: boolean }) {
  return (
    <Dialog.Close
      aria-label="Close"
      disabled={disabled}
      className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground enabled:hover:bg-secondary enabled:hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <path
          d="M3 3l8 8M11 3l-8 8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </Dialog.Close>
  );
}

/**
 * A renewal that signed in as another account than the connection's. Nothing changed, and the
 * person may have meant either thing: renewing with the right account, or connecting the other
 * one. Both are said. The callback page knows neither the app nor the account, so both are optional.
 */
export function differentAccountCopy(
  appName?: string | null,
  identity?: string | null,
): { title: string; description: string } {
  return {
    title: appName ? `That was a different ${appName} account` : "That was a different account",
    description: `Nothing changed. To renew, sign in ${
      identity ? `as ${identity}` : "with the account this connection already uses"
    }. To use the other account, add it as a new connection.`,
  };
}

/**
 * What the waiting step says for where the attempt stands; `attempt` is absent until the first
 * poll. `identity` is the account a renewal must sign in as, when known. `inBrowser` is a desktop
 * sign-in, which happens in the system browser rather than a window this page opened.
 */
export function waitingCopy(
  appName: string,
  attempt: Pick<Attempt, "status" | "reason"> | undefined,
  identity?: string | null,
  inBrowser = false,
): { title: string; description: string } {
  if (attempt?.status === "failed" && attempt.reason === "different_account") {
    return differentAccountCopy(appName, identity);
  }
  if (attempt?.status === "failed" || attempt?.status === "expired") {
    return {
      title:
        attempt.status === "failed" ? `${appName} didn't connect` : "That sign-in link expired",
      description: "Nothing was saved. Try again when you're ready.",
    };
  }
  return {
    title: `Signing in on ${appName}'s own page`,
    description: inBrowser
      ? `Sign in and allow access in your browser, or open ${appName} again if it didn't appear. This updates on its own when you're done.`
      : `Sign in and allow access in the window that opened, or open ${appName} here if none did. This updates on its own when you're done.`,
  };
}

/**
 * Waits for the sign-in to settle. The sign-in window reports back at once when it can; polling
 * every two seconds is the fallback and the only path when the sign-in happened elsewhere.
 * Exported because renewing access on the details page waits the same way. `overLabel` names
 * what `onBack` does once the attempt failed or expired: starting again when adding, closing the
 * dialog when renewing.
 */
export function WaitingStep({
  app,
  attemptId,
  redirectUrl,
  context,
  onConnected,
  onBack,
  backLabel = "Back",
  overLabel = "Start again",
  identity,
}: {
  app: ConnectionApp;
  attemptId: string;
  redirectUrl: string;
  context: ConnectionsContext;
  onConnected: (connectionId: string) => void;
  onBack: () => void;
  backLabel?: string | undefined;
  overLabel?: string | undefined;
  /** The account a renewal must sign in as, named if it signs in as another. */
  identity?: string | null | undefined;
}) {
  const attempt = useQuery({
    queryKey: ["connection-attempt", context.scope.userId, context.scope.workspaceId, attemptId],
    queryFn: () => pollAttempt(context, attemptId),
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== "waiting" ? false : 2000,
    refetchIntervalInBackground: true,
    retry: false,
    gcTime: 0,
  });
  const settled = useRef(false);
  useEffect(() => {
    const data = attempt.data;
    if (!settled.current && data?.status === "connected" && data.connectionId) {
      settled.current = true;
      onConnected(data.connectionId);
    }
  }, [attempt.data, onConnected]);
  const { refetch } = attempt;
  useEffect(() => listenForAttempt(attemptId, () => void refetch()), [attemptId, refetch]);

  const status = attempt.data?.status;
  const over = status === "failed" || status === "expired";
  // The shell keeps its window on the app, so a link could not open there; it asks for the browser.
  const inBrowser = signInReturn() === "desktop";
  const copy = waitingCopy(app.name, attempt.data, identity, inBrowser);
  return (
    <>
      <div className="flex items-start gap-4 px-6 pt-5 pb-4">
        <AppMark app={app} size={40} />
        <div className="min-w-0 flex-1" aria-live="polite">
          <Dialog.Title>{copy.title}</Dialog.Title>
          <Dialog.Description className="mt-1">{copy.description}</Dialog.Description>
        </div>
        <CloseButton />
      </div>
      <div className="space-y-4 border-t border-border px-6 py-5">
        {!over && (
          <output className="m-0 flex items-center gap-2.5 text-[13px]/5 text-muted-foreground">
            <Loader />
            Waiting for {app.name}…
          </output>
        )}
        <ErrorNotice error={attempt.error ? normalizeAppError(attempt.error) : null} />
      </div>
      <Dialog.Footer>
        <Button variant="outline" onClick={onBack}>
          {over ? overLabel : backLabel}
        </Button>
        {!over && inBrowser && (
          <Button variant="outline" onClick={() => openSignIn(redirectUrl)}>
            Open {app.name}
          </Button>
        )}
        {!over && !inBrowser && (
          <Button
            variant="outline"
            render={
              <a
                href={redirectUrl}
                target="_blank"
                rel="noopener"
                aria-label={`Open ${app.name}`}
              />
            }
          >
            Open {app.name}
          </Button>
        )}
      </Dialog.Footer>
    </>
  );
}
